/**
 * Distributes a total value as evenly as possible among a specified number of items.
 * Any remainder is added to the first item (index 0).
 *
 * @param total The total value to distribute.
 * @param count The number of items to distribute the value among.
 * @returns An array of numbers representing the distributed values for each item.
 */
export function distributeTotal(total: number, count: number): number[] {
  const safeTotal = total || 0;
  if (count <= 0) {
    return [];
  }

  const remainder = safeTotal % count;
  const baseValue = (safeTotal - remainder) / count;

  return Array.from({ length: count }, (_, i) => {
    return i === 0 ? baseValue + remainder : baseValue;
  });
}

/**
 * Recalculates the distribution of a total value across a list of items after one item has been changed.
 *
 * @param total The new total value.
 * @param currentValues An array of the current numeric values for each item.
 * @param changedIndex The index of the item that was manually changed.
 * @returns A new array of numbers with the recalculated distribution.
 */
export function redistributeTotal(
  total: number,
  currentValues: number[],
  changedIndex: number,
): number[] {
  const count = currentValues.length;
  if (count === 0) {
    return [];
  }

  const safeTotal = total || 0;
  const changedValue = currentValues[changedIndex] || 0;

  // If the changed value exceeds the total, redistribute the total evenly across all items.
  if (changedValue > safeTotal) {
    return distributeTotal(safeTotal, count);
  }

  const newValues = [...currentValues];
  const remainingTotal = safeTotal - changedValue;
  const remainingCount = count - 1;

  if (remainingCount <= 0) {
    return newValues;
  }

  const remainder = remainingTotal % remainingCount;
  const baseValue = (remainingTotal - remainder) / remainingCount;

  // Give the remainder to index 0,
  // unless index 0 was the item being changed, in which case, give it to index 1.
  const remainderTargetIndex = changedIndex === 0 ? 1 : 0;

  for (let i = 0; i < count; i++) {
    if (i !== changedIndex) {
      let value = baseValue;
      if (i === remainderTargetIndex) {
        value += remainder;
      }
      newValues[i] = value;
    }
  }
  return newValues;
}
